import { AccordionContext } from "./AccordionContext";
import { AccordionItem } from "./AccordionItem";
import { Box } from "../../box";
import { forwardRef } from "react";
import { Keys, cssModule, isNil, isNumber, mergeProps, useAutoFocusChild, useControllableState, useEventCallback, useFocusManager, useFocusScope, useId, useKeyboardNavigation, useMergedRefs } from "../../shared";
import { useAccordionItems } from "./useAccordionItems";
import { jsx as _jsx } from "react/jsx-runtime";
const DefaultElement = "div";
export function InnerAccordion({
  as = DefaultElement,
  autoFocus,
  children,
  defaultExpandedKeys,
  expandedKeys: expandedKeysProp,
  expansionMode = "single",
  forwardedRef,
  id,
  onExpansionChange,
  variant = "borderless",
  ...rest
}) {
  const [expandedKeys, setExpandedKeys] = useControllableState(expandedKeysProp, defaultExpandedKeys, []);
  const [focusScope, setFocusRef] = useFocusScope();
  const containerRef = useMergedRefs(setFocusRef, forwardedRef);
  const accordionId = useId(id, "o-ui-accordion");
  const items = useAccordionItems(children, accordionId);
  const focusManager = useFocusManager(focusScope);
  useAutoFocusChild(focusManager, {
    delay: isNumber(autoFocus) ? autoFocus : undefined,
    isDisabled: !autoFocus
  });
  const navigationProps = useKeyboardNavigation(focusManager, {
    first: [Keys.home],
    last: [Keys.end],
    next: [Keys.arrowDown],
    previous: [Keys.arrowUp]
  });
  const handleToggle = useEventCallback((event, toggledKey) => {
    let newKeys;

    if (!expandedKeys.includes(toggledKey)) {
      if (expansionMode === "multiple") {
        newKeys = [...expandedKeys, toggledKey];
      } else {
        newKeys = [toggledKey];
      }
    } else {
      newKeys = expandedKeys.filter(x => x !== toggledKey);
    }

    setExpandedKeys(newKeys);

    if (!isNil(onExpansionChange)) {
      onExpansionChange(event, newKeys);
    }
  });
  return /*#__PURE__*/_jsx(Box, { ...mergeProps(rest, {
      as,
      className: cssModule("o-ui-accordion", variant),
      id: accordionId,
      ref: containerRef
    }, navigationProps),
    children: /*#__PURE__*/_jsx(AccordionContext.Provider, {
      value: {
        expandedKeys: expandedKeys,
        onToggle: handleToggle
      },
      children: items.map(({
        header,
        id: itemId,
        key,
        panel
      }) => /*#__PURE__*/_jsx(AccordionItem, {
        item: {
          header,
          id: itemId,
          key,
          panel
        }
      }, key))
    })
  });
}
InnerAccordion.defaultElement = DefaultElement;
export const Accordion = /*#__PURE__*/forwardRef((props, ref) => /*#__PURE__*/_jsx(InnerAccordion, { ...props,
  forwardedRef: ref
}));